import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { X, User, Shield, MapPin, KeyRound, Cpu, Wrench, Droplets, Battery, Calendar, Flag, Clock, StickyNote, Navigation, FileText, History, ShieldCheck } from 'lucide-react';
import { serviceCallsAPI } from '@shared/apiClient';
import { formatDate, formatDateTime, getClientDisplayName, getStatusColor, getStatusText } from '@shared/utils';
import { DetailSection, DetailRow, StatPill, getDirectionsUrl } from './DetailSection';

const HistoryStatusBadge = ({ status, resolution }) => {
  if (resolution === 'skipped') {
    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
        Skipped
      </span>
    );
  }
  if (resolution === 'bumped') {
    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
        Bumped
      </span>
    );
  }

  const statusConfig = {
    assigned: { color: 'bg-blue-100 text-blue-800', label: 'Assigned' },
    scheduled: { color: 'bg-yellow-100 text-yellow-800', label: 'Scheduled' },
    in_progress: { color: 'bg-purple-100 text-purple-800', label: 'In Progress' },
    completed: { color: 'bg-green-100 text-green-800', label: 'Completed' },
    cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' },
    rescheduled: { color: 'bg-orange-100 text-orange-800', label: 'Rescheduled' },
  };
  const config = statusConfig[status] || statusConfig.assigned;
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
      {config.label}
    </span>
  );
};

export default function ClientDetailsModal({ client, isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    if (!isOpen) return undefined;
    setActiveTab('details');
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const clientSafe = client || {};
  const displayName = client ? getClientDisplayName(client) : 'Loading...';
  const contactName = [clientSafe.contactFirstName, clientSafe.contactLastName].filter(Boolean).join(' ');

  const formatBoolean = (value) => (value ? 'Yes' : 'No');

  const formatCoordinate = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = typeof value === 'string' ? parseFloat(value) : value;
    if (Number.isNaN(parsed)) return value;
    return parsed.toFixed(6);
  };

  const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const getServiceMonths = (months) => {
    if (!months) return null;
    const normalized = Array.isArray(months) ? months : [];
    return normalized.map((m) => monthLabels[m - 1]).filter(Boolean).join(', ');
  };

  const renderFlags = () => {
    if (!Array.isArray(clientSafe.flags) || clientSafe.flags.length === 0) return null;
    return clientSafe.flags
      .map((flag) => {
        const label = flag.icon ? `${flag.icon} ${flag.name}` : flag.name;
        return flag.isActive === false ? `${label} (inactive)` : label;
      })
      .join(', ');
  };

  const { data: serviceHistoryData, isLoading: isServiceHistoryLoading } = useQuery(
    ['clientServiceHistory', clientSafe.id],
    () => serviceCallsAPI.getServiceCalls({ clientId: clientSafe.id, limit: 1000, sortBy: 'scheduledDate', sortOrder: 'DESC' }),
    { enabled: isOpen && !!clientSafe.id && activeTab === 'history' }
  );

  const serviceHistory = serviceHistoryData?.data?.serviceCalls || [];

  const serviceCallIds = serviceHistory.map((sc) => sc.id);
  const { data: serviceHistoryDetailsData, isLoading: isLoadingHistoryDetails } = useQuery(
    ['clientServiceHistoryDetails', clientSafe.id, serviceCallIds],
    async () => {
      const responses = await Promise.all(
        serviceCallIds.map((id) => serviceCallsAPI.getServiceCall(id))
      );
      return responses
        .map((response) => response.data?.serviceCall)
        .filter(Boolean);
    },
    { enabled: isOpen && !!clientSafe.id && activeTab === 'history' && serviceCallIds.length > 0 }
  );

  const fullServiceCalls = serviceHistoryDetailsData?.length ? serviceHistoryDetailsData : serviceHistory;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-50">
      <div className="fixed inset-0 flex h-full w-full flex-col">
        {/* Gradient Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-primary-200">Customer Details</p>
              <h3 className="text-xl font-bold text-white mt-1">{displayName}</h3>
            </div>
            <button
              onClick={onClose}
              className="rounded-full p-2 text-primary-200 hover:bg-white/10 hover:text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          {client && (
            <div className="flex flex-wrap gap-2 mt-4">
              <StatPill
                label="Status"
                value={clientSafe.isActive ? 'Active' : 'Inactive'}
                variant={clientSafe.isActive ? 'success' : 'danger'}
              />
              <StatPill
                label="Type"
                value={clientSafe.isCommercial ? 'Commercial' : 'Residential'}
              />
              {clientSafe.isContractClient && (
                <StatPill label="Contract" value="Yes" variant="success" />
              )}
              {clientSafe.generatorType && (
                <StatPill label="Generator" value={clientSafe.generatorType} />
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-white px-6">
          <nav className="flex -mb-px gap-1">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-3 px-5 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === 'details'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="inline h-4 w-4 mr-1.5" />
              Details
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-3 px-5 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === 'history'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <History className="inline h-4 w-4 mr-1.5" />
              History
              {serviceHistory.length > 0 && (
                <span className="ml-1.5 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                  {serviceHistory.length}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto overscroll-contain touch-pan-y px-4 py-5">
          {!client ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              Loading customer details...
            </div>
          ) : activeTab === 'details' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
              <DetailSection icon={User} title="Contact Information" color="blue" span>
                <DetailRow label="Company Name" value={clientSafe.companyName} />
                <DetailRow label="Contact Name" value={contactName} />
                <DetailRow label="Email" value={clientSafe.email ? (
                  <a href={`mailto:${clientSafe.email}`} className="text-blue-600 hover:underline">{clientSafe.email}</a>
                ) : null} />
                <DetailRow label="Phone" value={clientSafe.phone ? (
                  <a href={`tel:${clientSafe.phone}`} className="text-blue-600 hover:underline">{clientSafe.phone}</a>
                ) : null} />
                <DetailRow label="Secondary Phone" value={clientSafe.secondPhone ? (
                  <a href={`tel:${clientSafe.secondPhone}`} className="text-blue-600 hover:underline">{clientSafe.secondPhone}</a>
                ) : null} />
              </DetailSection>

              <DetailSection icon={Shield} title="Status & Classification" color="green">
                <DetailRow label="Active" value={formatBoolean(clientSafe.isActive)} />
                <DetailRow label="Commercial" value={formatBoolean(clientSafe.isCommercial)} />
                <DetailRow label="Contract Customer" value={formatBoolean(clientSafe.isContractClient)} />
              </DetailSection>

              <DetailSection icon={KeyRound} title="Access Information" color="slate">
                <DetailRow label="Omni Matrix" value={clientSafe.omniMatrix ? 'Yes' : 'No'} />
                <DetailRow label="Omni Matrix ID" value={clientSafe.omniMatrixId} />
                <DetailRow label="Gate Code Required" value={clientSafe.gateCode ? 'Yes' : 'No'} />
                <DetailRow label="Gate Code" value={clientSafe.gateCodeValue} />
              </DetailSection>

              <DetailSection icon={MapPin} title="Address" color="amber">
                <DetailRow label="Street Address" value={clientSafe.address} />
                <DetailRow label="City" value={clientSafe.city} />
                <DetailRow label="State" value={clientSafe.state} />
                <DetailRow label="ZIP Code" value={clientSafe.zipCode} />
                <DetailRow label="Latitude" value={formatCoordinate(clientSafe.latitude)} />
                <DetailRow label="Longitude" value={formatCoordinate(clientSafe.longitude)} />
              </DetailSection>

              <DetailSection icon={Cpu} title="Generator Information" color="green">
                <DetailRow label="Type" value={clientSafe.generatorType} />
                <DetailRow label="Model" value={clientSafe.generatorModel} />
                <DetailRow label="Serial Number" value={clientSafe.generatorSerialNumber} />
                <DetailRow label="Size / Capacity" value={clientSafe.generatorSize} />
                <DetailRow label="Exercise Time" value={clientSafe.generatorExerciseTime} />
              </DetailSection>

              <DetailSection icon={Wrench} title="Maintenance Parts" color="orange">
                <DetailRow label="Air Filter" value={clientSafe.airFilter} />
                <DetailRow label="Fuel Filter 1" value={clientSafe.fuelFilter1} />
                <DetailRow label="Fuel Filter 2" value={clientSafe.fuelFilter2} />
                <DetailRow label="Oil Filter 1" value={clientSafe.oilFilter1} />
                <DetailRow label="Oil Filter 2" value={clientSafe.oilFilter2} />
              </DetailSection>

              <DetailSection icon={Droplets} title="Fluids" color="cyan">
                <DetailRow label="Fuel Type" value={clientSafe.fuelType} />
                <DetailRow label="Fuel Additive" value={clientSafe.fuelAdditive} />
                <DetailRow label="Oil Type" value={clientSafe.oilType} />
                <DetailRow label="Oil Amount" value={clientSafe.oilAmount} />
              </DetailSection>

              <DetailSection icon={Battery} title="Battery" color="yellow">
                <DetailRow label="Battery Size" value={clientSafe.batterySize} />
                <DetailRow label="Battery Date Code" value={clientSafe.batteryDateCode} />
                <DetailRow label="Battery Date Changed" value={clientSafe.batteryDateChanged ? formatDate(clientSafe.batteryDateChanged) : null} />
              </DetailSection>

              <DetailSection icon={Calendar} title="Service Information" color="purple">
                <DetailRow label="Service Months" value={getServiceMonths(clientSafe.serviceCycleMonths)} />
                <DetailRow label="Oil Change Month" value={clientSafe.oilChangeMonth ? monthLabels[clientSafe.oilChangeMonth - 1] : null} />
                <DetailRow label="Last Service" value={clientSafe.lastContractServiceDate ? formatDate(clientSafe.lastContractServiceDate) : null} />
                <DetailRow label="Next Service" value={clientSafe.nextContractServiceDate ? formatDate(clientSafe.nextContractServiceDate) : null} />
              </DetailSection>

              {renderFlags() && (
                <DetailSection icon={Flag} title="Client Flags" color="purple">
                  <DetailRow label="Flags" value={renderFlags()} />
                </DetailSection>
              )}

              {clientSafe.notes && (
                <DetailSection icon={StickyNote} title="Notes" color="slate" span>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{clientSafe.notes}</p>
                </DetailSection>
              )}
            </div>
          ) : (
            <div className="max-w-4xl mx-auto">
              {isServiceHistoryLoading || isLoadingHistoryDetails ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
                  <p className="mt-3 text-sm text-gray-500">Loading service history...</p>
                </div>
              ) : fullServiceCalls.length === 0 ? (
                <div className="text-center py-16">
                  <Clock className="mx-auto h-12 w-12 text-gray-300" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">No Service History</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    This client doesn&apos;t have any service calls yet.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {fullServiceCalls
                    .sort((a, b) => {
                      const dateA = new Date(a.completedDate || a.scheduledDate || a.createdAt);
                      const dateB = new Date(b.completedDate || b.scheduledDate || b.createdAt);
                      return dateB - dateA;
                    })
                    .map((serviceCall) => {
                      const displayDate = serviceCall.completedDate || serviceCall.scheduledDate || serviceCall.createdAt;
                      const visits = Array.isArray(serviceCall.visits) ? serviceCall.visits : [];
                      const statusBorderColor = serviceCall.resolution === 'skipped'
                        ? 'border-l-amber-400'
                        : serviceCall.resolution === 'bumped'
                          ? 'border-l-orange-400'
                          : serviceCall.status === 'completed'
                            ? 'border-l-emerald-400'
                            : serviceCall.status === 'in_progress'
                              ? 'border-l-blue-400'
                              : serviceCall.status === 'cancelled'
                                ? 'border-l-red-400'
                                : 'border-l-slate-300';

                      return (
                        <div key={serviceCall.id} className={`rounded-lg border border-slate-200 bg-white p-4 border-l-4 ${statusBorderColor} space-y-3`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-wrap">
                              <HistoryStatusBadge status={serviceCall.status} resolution={serviceCall.resolution} />
                              {serviceCall.isFinalized && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                  Finalized
                                </span>
                              )}
                              {serviceCall.type && (
                                <span className="text-xs text-gray-500">
                                  {serviceCall.type.name}
                                </span>
                              )}
                              {serviceCall.invoiceNumber && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                  Inv# {serviceCall.invoiceNumber}
                                </span>
                              )}
                              {serviceCall.isWarranty && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  <ShieldCheck className="h-3 w-3" />
                                  Warranty
                                </span>
                              )}
                            </div>
                            {displayDate && (
                              <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                                {formatDate(displayDate)}
                              </span>
                            )}
                          </div>

                          <div className="text-sm text-gray-600 space-y-0.5">
                            {serviceCall.scheduledDate && (
                              <div><span className="font-medium">Scheduled:</span> {formatDateTime(serviceCall.scheduledDate)}</div>
                            )}
                            {serviceCall.completedDate && (
                              <div><span className="font-medium">Completed:</span> {formatDateTime(serviceCall.completedDate)}</div>
                            )}
                          </div>

                          {serviceCall.description && (
                            <p className="text-sm text-gray-700">{serviceCall.description}</p>
                          )}
                          {serviceCall.workPerformed && (
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Work Performed:</span> {serviceCall.workPerformed}
                            </p>
                          )}

                          {visits.length > 0 && (
                            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mt-2">
                              <div className="flex items-center gap-2 mb-2">
                                <History className="h-4 w-4 text-amber-700" />
                                <h4 className="font-semibold text-sm text-gray-900">
                                  Visit History ({visits.length} visit{visits.length > 1 ? 's' : ''})
                                </h4>
                              </div>
                              <div className="space-y-2">
                                {visits.map((visit) => (
                                  <div key={visit.id} className="bg-white rounded-md p-3 border border-amber-100">
                                    <div className="flex items-center justify-between mb-1.5">
                                      <span className="font-medium text-sm text-gray-900">Visit #{visit.visitNumber}</span>
                                      <span className="text-xs text-gray-600">{formatDate(visit.visitDate)}</span>
                                    </div>
                                    {visit.technician && (
                                      <p className="text-sm text-gray-600 mb-1">
                                        <span className="font-medium">Technician:</span> {visit.technician.firstName} {visit.technician.lastName}
                                      </p>
                                    )}
                                    {visit.type && (
                                      <p className="text-sm text-gray-600 mb-1">
                                        <span className="font-medium">Type:</span> {visit.type.name}
                                      </p>
                                    )}
                                    {visit.workPerformed && (
                                      <div className="mb-1">
                                        <p className="text-sm font-medium text-gray-700">Work Performed:</p>
                                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{visit.workPerformed}</p>
                                      </div>
                                    )}
                                    {visit.outcome === 'needs_return' && visit.reasonForReturn && (
                                      <div className="bg-yellow-50 rounded p-2 mt-1.5">
                                        <p className="text-sm font-medium text-yellow-900">Reason for Return:</p>
                                        <p className="text-sm text-yellow-800">{visit.reasonForReturn}</p>
                                      </div>
                                    )}
                                    {visit.partsUsed && visit.partsUsed.length > 0 && (
                                      <div className="mt-1.5">
                                        <p className="text-sm font-medium text-gray-700">Parts Used:</p>
                                        <ul className="text-sm text-gray-600 list-disc list-inside">
                                          {visit.partsUsed.map((part, partIdx) => (
                                            <li key={partIdx}>
                                              {part.itemNumber || part.name} - Qty: {part.quantity} {part.cost && `($${parseFloat(part.cost).toFixed(2)})`}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Show root-level parts not already covered by visits */}
                          {(() => {
                            const rootParts = Array.isArray(serviceCall.partsUsed) ? serviceCall.partsUsed : [];
                            const visitParts = visits.flatMap(v => Array.isArray(v.partsUsed) ? v.partsUsed : []);
                            const visitPartKeys = new Set(visitParts.map(p => `${p.itemNumber || p.name}|${p.quantity}|${p.cost}`));
                            const extraParts = rootParts.filter(p => !visitPartKeys.has(`${p.itemNumber || p.name}|${p.quantity}|${p.cost}`));
                            if (extraParts.length === 0) return null;
                            return (
                              <div className="mt-2">
                                <p className="text-sm font-medium text-gray-700 mb-1">Parts Used:</p>
                                <ul className="text-sm text-gray-600 list-disc list-inside">
                                  {extraParts.map((part, partIdx) => (
                                    <li key={partIdx}>
                                      {part.itemNumber || part.name} - Qty: {part.quantity} {part.cost && `($${parseFloat(part.cost).toFixed(2)})`}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-slate-200 bg-white px-6 py-4">
          <div>
            {client && (() => {
              const dirUrl = getDirectionsUrl({ latitude: clientSafe.latitude, longitude: clientSafe.longitude, address: clientSafe.address, city: clientSafe.city, state: clientSafe.state, zipCode: clientSafe.zipCode });
              return dirUrl ? (
                <a
                  href={dirUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-400"
                >
                  <Navigation className="h-4 w-4 mr-1.5" />
                  Directions
                </a>
              ) : null;
            })()}
          </div>
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
